import { Accordion, Button, Card, Grid } from '@design-systems/canvas-ui'
import {
    MetricDetail,
    useDimensionDetails,
    useDimensions,
    TimeDimension,
    UseDimensionDetailResponse,
} from 'hooks/operations/useDimensions'
import { useEssentials } from 'hooks/utils/useEssentials'
import { isStore } from 'utils/location'
import { useMemo } from 'react'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { TimePeriods } from '../../components/timeperiods'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { OperationsDetails } from './operations-details'
import {
    getColorFromDetail,
    getTileHeaderProps,
    buildDetailPeriodsIndex,
    sortTimePeriods,
    splitPeriods,
} from './helpers'
import { useNavigate } from 'react-router-dom'
import { UrlParams } from '../../enums/url-params'
import { translateItem } from 'locales/utils'
import { useOperationsContext } from './operations-context'
import { OperationsMetricsTile } from 'types/api'

export const OperationsMetrics = () => {
    const navigate = useNavigate()
    const { t, currentLocation, env, session } = useEssentials()
    const { activeTimePeriod, setActiveTimePeriod } = useOperationsContext()
    const { operationalMetrics } = TRANSLATION_KEYS
    const isStoreLocation = isStore(currentLocation)
    const timeDimension = activeTimePeriod || 'Prior Day'

    // Pass the translation KEYS object (not localized values) to translateItem
    const operationsCardsTranslations = operationalMetrics.operationsCards

    const availableTimeDimensions = sortTimePeriods([
        'Prior Day',
        'Week to Day',
        'Prior Week',
        'Month to Day',
        'Prior Month',
        'Year to Day',
    ])

    const metricHeaders = {
        atGoal: t(operationalMetrics.headers.atGoal),
        belowGoal: t(operationalMetrics.headers.belowGoal),
        needsAttention: t(operationalMetrics.headers.needsAttention),
    }

    const {
        data: dimensionsData,
        isLoading,
        isError,
        error,
        refetch,
    } = useDimensions({
        env,
        accessToken: session?.accessToken ?? '',
        filters: {
            ...(isStoreLocation && {
                location_id: currentLocation.replace(/\D/g, ''),
            }),
            card_type: 'new',
        },
    })

    const tileData: OperationsMetricsTile[] = (
        dimensionsData?.filter(
            (item: MetricDetail) =>
                item.page_type === 'tile' &&
                item.time_period?.includes(timeDimension),
        ) || []
    ).map((tile: MetricDetail) => ({
        ...tile,
        time_dimension: timeDimension as TimeDimension,
    }))

    const detailsResult = useDimensionDetails({
        env,
        accessToken: session?.accessToken ?? '',
        tiles: tileData,
        location: currentLocation,
    })

    const handleTimeDimensionChange = (timePeriod: string) => {
        setActiveTimePeriod(timePeriod)
    }

    const periodsIndex = useMemo(
        () => buildDetailPeriodsIndex(dimensionsData),
        [dimensionsData],
    )

    const collectAllDetailPeriodsForMetric = (metricName: string): string[] => {
        return periodsIndex[metricName] ?? []
    }

    if (isLoading) {
        return (
            <div
                className="placeholder-container hc8-pt-6x"
                data-testid="placeholder"
            >
                <BasicPlaceholder type="table" rows={5} columns={3} />
            </div>
        )
    }

    if (isError) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    const buildDetailsUrl = (detail: UseDimensionDetailResponse) => {
        const metricName = detail.metric_name,
            params = new URLSearchParams()
        params.set(UrlParams.METRIC_NAME, metricName)

        // Prefer the detail/exception entry whose time_period includes the
        // active timeDimension; fall back to the first candidate so card IDs
        // are always present even when the DB rows are out of sync.
        const detailCandidates = (dimensionsData ?? []).filter(
            (it) => it.page_type === 'detail' && it.metric_name === metricName,
        )
        const detailData =
            detailCandidates.find((d) =>
                splitPeriods(d.time_period).includes(timeDimension),
            ) ||
            detailCandidates[0] ||
            {}

        const exceptionCandidates = (dimensionsData ?? []).filter(
            (it) =>
                it.page_type === 'exception' && it.metric_name === metricName,
        )
        const exceptionData =
            exceptionCandidates.find((d) =>
                splitPeriods(d.time_period).includes(timeDimension),
            ) ||
            exceptionCandidates[0] ||
            {}

        const periodsFromMetric = collectAllDetailPeriodsForMetric(metricName)

        if (periodsFromMetric.length) {
            params.set(UrlParams.TIME_PERIODS, periodsFromMetric.join(','))
        }

        if (timeDimension) {
            params.set(UrlParams.ACTIVE_TIME_PERIOD, timeDimension)
        }

        if (detailData.card_id_non_store) {
            params.set(
                UrlParams.RGD_DETAIL_CARD_ID,
                String(detailData.card_id_non_store),
            )
        }
        if (detailData.card_id_store) {
            params.set(
                UrlParams.STORE_DETAIL_CARD_ID,
                String(detailData.card_id_store),
            )
        }
        if (exceptionData.card_id_non_store) {
            params.set(
                UrlParams.RGD_EXCEPTION_CARD_ID,
                String(exceptionData.card_id_non_store),
            )
        }
        if (exceptionData.card_id_store) {
            params.set(
                UrlParams.STORE_EXCEPTION_CARD_ID,
                String(exceptionData.card_id_store),
            )
        }

        params.set(UrlParams.LOCATION_ID, currentLocation)
        return `/operations/details?${params.toString()}`
    }

    return (
        <div className="operations-metrics-container">
            <TimePeriods
                timePeriods={availableTimeDimensions}
                activeTimePeriod={timeDimension}
                onTabSelect={handleTimeDimensionChange}
                isTimePeriodSticky={false}
                page="operations"
            />
            <Grid.Container>
                {detailsResult.length === 0 ? (
                    <Grid.Item xs={12} className="hc8-pa-3x">
                        <ErrorAlert variant="no-data" />
                    </Grid.Item>
                ) : (
                    [...detailsResult]
                        .sort((a, b) => a.stack_rank - b.stack_rank)
                        .map((detail) => {
                            const color = getColorFromDetail(detail),
                                { className, text } = getTileHeaderProps(
                                    color,
                                    metricHeaders,
                                )
                            const detailsURL = buildDetailsUrl(detail)

                            return (
                                <Grid.Item
                                    key={detail.metric_name}
                                    className="hc8-pt-3x hc8-pr-8x hc8-pl-8x"
                                    size={{
                                        xs: 12,
                                    }}
                                >
                                    {detail ? (
                                        <Card className="hc8-ta-center">
                                            {detail.isLoading ? (
                                                <BasicPlaceholder.Rect height="200px" />
                                            ) : (
                                                <>
                                                    <Grid.Item
                                                        className={`operations-metric-tile-header ${className}`}
                                                        size={{
                                                            xs: 12,
                                                        }}
                                                    >
                                                        {text || ''}
                                                    </Grid.Item>

                                                    <Grid.Item
                                                        className={
                                                            'hc8-fs-100 hc8-pa-3x'
                                                        }
                                                    >
                                                        {translateItem({
                                                            t,
                                                            item: detail.metric_name,
                                                            translation:
                                                                operationsCardsTranslations,
                                                        })}
                                                    </Grid.Item>

                                                    <Grid.Item
                                                        className={'hc8-fs-200'}
                                                    >
                                                        {/* Conditionally hide the value for Total Priority Fill if goal met */}
                                                        {detail.metric_name ===
                                                            'Total Priority Fill %' &&
                                                        className ===
                                                            'hc8-bg-success'
                                                            ? null
                                                            : detail?.tile
                                                                  ?.rows?.[0]?.[1]
                                                                  ?.value ||
                                                              '-'}
                                                    </Grid.Item>

                                                    <Grid.Item
                                                        className={
                                                            'hc8-fs-100 hc8-pt-3x hc8-pl-6x hc8-pr-6x '
                                                        }
                                                    >
                                                        {`${translateItem({
                                                            t,
                                                            item:
                                                                detail?.tile
                                                                    ?.headings?.[0]
                                                                    ?.value ||
                                                                '',
                                                            translation:
                                                                operationsCardsTranslations,
                                                        })}:`}
                                                        {` ${
                                                            detail?.tile
                                                                ?.rows?.[0]?.[0]
                                                                ?.value || '-'
                                                        }`}
                                                    </Grid.Item>

                                                    <Grid.Item
                                                        size={{
                                                            xs: 12,
                                                        }}
                                                    >
                                                        <Button
                                                            className={
                                                                'hc8-fs-100'
                                                            }
                                                            onClick={() => {
                                                                void navigate(
                                                                    detailsURL,
                                                                )
                                                            }}
                                                            variant="ghost"
                                                        >
                                                            {t(
                                                                operationalMetrics.seeChartView,
                                                            )}
                                                        </Button>
                                                    </Grid.Item>

                                                    <Grid.Item
                                                        className={
                                                            'hc8-pl-6x hc8-pr-6x'
                                                        }
                                                        size={{
                                                            xs: 12,
                                                        }}
                                                    >
                                                        <Accordion
                                                            spacing="dense"
                                                            className="hc8-pa-4x"
                                                            headerSlot={
                                                                <p>
                                                                    {t(
                                                                        operationalMetrics.historicalDetails,
                                                                    )}
                                                                </p>
                                                            }
                                                        >
                                                            <div className="hc8-pv-3x">
                                                                <OperationsDetails
                                                                    metric={tileData.find(
                                                                        (
                                                                            data,
                                                                        ) =>
                                                                            data.metric_name ===
                                                                            detail.metric_name,
                                                                    )}
                                                                />
                                                            </div>
                                                        </Accordion>
                                                    </Grid.Item>
                                                </>
                                            )}
                                        </Card>
                                    ) : null}
                                </Grid.Item>
                            )
                        })
                )}
            </Grid.Container>
        </div>
    )
}
